import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { initialState, type AppState, isStale, nextSimId, reducer, simBasis, simLabel } from './state/app';
import { configLabel, mountsOf } from './state/configs';
import { analyze, buildRequest } from './state/derived';
import { load, save } from './state/persist';
import { startSimulation, type Running } from './worker/client';
import { About } from './ui/About';
import { ComponentActions, ComponentPalette, ComponentTree, selection } from './ui/ComponentList';
import { DesignView } from './ui/DesignView';
import { FloatingDialog, ModalDialog } from './ui/Dialog';
import { FileImport, downloadRocket, type FileImportHandle } from './ui/FileMenu';
import { makeFormat } from './ui/format';
import { MenuBar, type MenuDef } from './ui/MenuBar';
import { ConfigsTab, MotorPicker, RenameConfig } from './ui/MotorPanel';
import { Preferences } from './ui/Preferences';
import { PropertyPanel } from './ui/PropertyPanel';
import { Results } from './ui/Results';
import { SimulationEditor } from './ui/SimulationPanel';
import { SimulationsTab, type RunningState, type SelectMode } from './ui/SimulationsTab';
import { SplitPane } from './ui/SplitPane';
import { StabilityPanel } from './ui/StabilityPanel';

type Tab = 'design' | 'config' | 'sims';

const TABS: ReadonlyArray<{ id: Tab; label: string }> = [
  { id: 'design', label: 'Diseño del cohete' },
  { id: 'config', label: 'Configuraciones' },
  { id: 'sims', label: 'Simulaciones de vuelo' },
];

/** Diálogo modal abierto; solo hay uno a la vez. El de componente es flotante y va aparte. */
type Modal =
  | { kind: 'sim'; id: string }
  | { kind: 'results'; id: string }
  | { kind: 'motor-picker'; configId: string; mountId: string }
  | { kind: 'rename-config'; id: string }
  | { kind: 'prefs' }
  | { kind: 'about' };

/** `initial` solo lo usan las pruebas para partir de un diseño distinto del vacío. */
export function App({ initial }: { readonly initial?: AppState } = {}): JSX.Element {
  const [state, dispatch] = useReducer(reducer, undefined, () => initial ?? initialState(load()));
  const [tab, setTab] = useState<Tab>('design');
  const [modal, setModal] = useState<Modal | undefined>();
  const [editing, setEditing] = useState(false);
  const [pickedSims, setPickedSims] = useState<readonly string[]>([]);
  const anchor = useRef<string | undefined>();
  const cancelQueue = useRef(false);
  const [running, setRunning] = useState<RunningState | undefined>();
  const [failure, setFailure] = useState<string | undefined>();
  const current = useRef<Running | undefined>();
  const files = useRef<FileImportHandle>(null);
  const queueing = useRef(false);

  const fmt = useMemo(() => makeFormat(state.units), [state.units]);
  const analysis = useMemo(() => analyze(state.rocket, state.motors), [state.rocket, state.motors]);
  // Simulaciones seleccionadas (la primera es la principal); sin elegir ninguna, la primera de la lista.
  const selectedIds = useMemo(() => {
    const valid = pickedSims.filter((id) => state.sims.some((s) => s.id === id));
    return valid.length > 0 || state.sims.length === 0 ? valid : [state.sims[0]!.id];
  }, [pickedSims, state.sims]);
  const selectedSim = state.sims.find((s) => s.id === selectedIds[0]);
  const closeModal = useCallback(() => setModal(undefined), []);

  // Las preferencias se guardan en el navegador; nada sale de él.
  useEffect(() => {
    save({ units: state.units, motors: state.motors, theme: state.theme });
  }, [state.units, state.motors, state.theme]);

  // El diseño no se guarda: con cambios sin exportar, el navegador pide confirmar antes de recargar o cerrar.
  useEffect(() => {
    if (!state.isDirty) return;
    const warn = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [state.isDirty]);

  useEffect(() => {
    document.documentElement.dataset['theme'] = state.theme;
  }, [state.theme]);

  useEffect(() => () => current.current?.cancel(), []);

  /** Lanza una simulación y devuelve cómo terminó; no abre diálogos. */
  async function runOne(id: string, waiting: number): Promise<'done' | 'cancelled' | 'stop'> {
    const sim = state.sims.find((x) => x.id === id);
    if (sim === undefined) return 'stop';
    setFailure(undefined);
    setRunning({ id, progress: undefined, waiting });
    const basis = simBasis(state, sim);
    const r = startSimulation(buildRequest(state, sim), (progress) => setRunning((cur) => (cur?.id === id ? { id, progress, waiting } : cur)));
    current.current = r;
    const outcome = await r.done;
    current.current = undefined;
    setRunning(undefined);
    if (outcome.kind === 'result') {
      dispatch({ type: 'sim-result', id, run: { result: outcome.result, basis } });
      return outcome.result.status === 'cancelled' ? 'cancelled' : 'done';
    }
    setFailure(outcome.kind === 'invalid' ? `${outcome.message}: ${outcome.issues.join('; ')}` : outcome.message);
    return 'stop';
  }

  /** Lanza varias simulaciones una tras otra; si es una sola, abre sus resultados al terminar. */
  async function onRun(ids: readonly string[]): Promise<void> {
    if (current.current !== undefined || queueing.current) {
      dispatch({ type: 'notice', level: 'warning', text: 'Ya hay una simulación en curso: espera a que termine o cancélala.' });
      return;
    }
    if (!analysis.valid) {
      dispatch({ type: 'notice', level: 'error', text: 'No se puede simular: corrige los errores del diseño.' });
      return;
    }
    queueing.current = true;
    cancelQueue.current = false;
    let last: string | undefined;
    for (let i = 0; i < ids.length; i++) {
      const end = await runOne(ids[i]!, ids.length - 1 - i);
      if (end !== 'stop') last = ids[i];
      if (end !== 'done' || cancelQueue.current) break;
    }
    queueing.current = false;
    // No se le quita al usuario otro diálogo que haya abierto mientras tanto.
    if (ids.length === 1 && last !== undefined) setModal((m) => m ?? { kind: 'results', id: last! });
  }

  const cancelRun = (): void => {
    cancelQueue.current = true;
    current.current?.cancel();
  };

  /** Selección de filas de la tabla de simulaciones: una, alternar con Ctrl y rango con Mayús. */
  const selectSim = (id: string, mode: SelectMode): void => {
    if (mode === 'toggle') setPickedSims(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [id, ...selectedIds]);
    else if (mode === 'range' && anchor.current !== undefined) {
      const from = state.sims.findIndex((x) => x.id === anchor.current);
      const to = state.sims.findIndex((x) => x.id === id);
      if (from < 0 || to < 0) setPickedSims([id]);
      else setPickedSims([id, ...state.sims.slice(Math.min(from, to), Math.max(from, to) + 1).map((x) => x.id).filter((x) => x !== id)]);
    } else setPickedSims([id]);
    if (mode !== 'range') anchor.current = id;
  };

  /** Abre las propiedades del componente seleccionado en el diálogo flotante (el equivalente a «Editar componente»). */
  const editSelected = (): void => {
    setModal(undefined);
    setTab('design');
    if (editing) document.querySelector<HTMLInputElement>('.dialog--floating .dialog__body input')?.focus();
    else setEditing(true);
  };
  const goTab = (t: Tab): void => {
    setEditing(false);
    setTab(t);
  };
  const newSim = (): void => {
    const id = nextSimId(state.sims);
    dispatch({ type: 'sim-add' });
    setPickedSims([id]);
    anchor.current = id;
    setModal({ kind: 'sim', id });
  };
  const simulate = (): void => {
    goTab('sims');
    if (selectedIds.length > 0) void onRun(selectedIds);
  };

  const { isStack, stackIndex, selected } = selection(state.rocket, state.selectedId);
  const busy = running !== undefined;
  const menus: readonly MenuDef[] = [
    {
      label: 'Archivo',
      items: [
        { label: 'Nuevo', onSelect: () => dispatch({ type: 'new' }) },
        { label: 'Ejemplo', onSelect: () => dispatch({ type: 'example' }) },
        { label: 'Importar diseño (.json)…', onSelect: () => files.current?.openJson() },
        { label: 'Importar de OpenRocket (.ork)…', onSelect: () => files.current?.openOrk() },
        { label: 'Exportar diseño', onSelect: () => {
            downloadRocket(state.rocket);
            dispatch({ type: 'exported' });
          } },
        { label: 'Preferencias…', onSelect: () => setModal({ kind: 'prefs' }) },
      ],
    },
    {
      label: 'Edición',
      items: [
        { label: 'Mover hacia arriba', disabled: !isStack || stackIndex <= 0, onSelect: () => dispatch({ type: 'move', id: state.selectedId!, delta: -1 }) },
        {
          label: 'Mover hacia abajo',
          disabled: !isStack || stackIndex < 0 || stackIndex >= state.rocket.stack.length - 1,
          onSelect: () => dispatch({ type: 'move', id: state.selectedId!, delta: 1 }),
        },
        { label: 'Editar componente', disabled: selected === undefined, onSelect: editSelected },
        { label: 'Borrar componente', disabled: selected === undefined, onSelect: () => dispatch({ type: 'remove', id: state.selectedId! }) },
      ],
    },
    {
      label: 'Analizar',
      items: [
        { label: 'Nueva simulación', onSelect: newSim },
        { label: 'Simular vuelo', disabled: busy || !analysis.valid || selectedSim === undefined, onSelect: simulate },
        { label: 'Cancelar simulación', disabled: !busy, onSelect: cancelRun },
      ],
    },
    { label: 'Ayuda', items: [{ label: 'Acerca de', onSelect: () => setModal({ kind: 'about' }) }] },
  ];

  /** Flechas, Inicio y Fin entre pestañas (patrón ARIA «tabs» con activación automática). */
  const onTabKey = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    e.preventDefault();
    const at = TABS.findIndex((t) => t.id === tab);
    const next = e.key === 'Home' ? 0 : e.key === 'End' ? TABS.length - 1 : (at + (e.key === 'ArrowRight' ? 1 : -1) + TABS.length) % TABS.length;
    goTab(TABS[next]!.id);
    e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus();
  };

  const modalSim = modal?.kind === 'sim' || modal?.kind === 'results' ? state.sims.find((s) => s.id === modal.id) : undefined;
  const activeConfig = state.configs.find((c) => c.id === state.activeConfigId) ?? state.configs[0]!;
  const pickerConfig = modal?.kind === 'motor-picker' ? state.configs.find((c) => c.id === modal.configId) : undefined;
  const pickerMount = modal?.kind === 'motor-picker' ? mountsOf(state.rocket).find((m) => m.id === modal.mountId) : undefined;
  const renamed = modal?.kind === 'rename-config' ? state.configs.find((c) => c.id === modal.id) : undefined;
  const flight = selectedSim?.run === undefined ? undefined : { summary: selectedSim.run.result.summary, stale: isStale(state, selectedSim) };

  return (
    <div className="window">
      <a className="skip-link" href="#main">
        Saltar al contenido
      </a>
      <header className="titlebar">
        <h1>
          Simulador de cohetes <span className="titlebar__name">— {state.rocket.name}</span>
        </h1>
      </header>
      <MenuBar menus={menus} />
      <FileImport ref={files} dispatch={dispatch} />

      <div className="tabbar">
        <div role="tablist" aria-label="Secciones" onKeyDown={onTabKey}>
          {TABS.map((t) => (
            <button key={t.id} type="button" role="tab" id={`tab-${t.id}`} aria-selected={tab === t.id} aria-controls="panel" tabIndex={tab === t.id ? 0 : -1} onClick={() => goTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="notices" role="status" aria-live="polite">
        {state.notices.map((n) => (
          <p key={n.id} className={`notice notice--${n.level}`}>
            {n.text}
            <button type="button" aria-label="Descartar aviso" onClick={() => dispatch({ type: 'dismiss-notice', id: n.id })}>
              ×
            </button>
          </p>
        ))}
      </div>

      <main id="main" tabIndex={-1}>
        <div className="panelwrap" role="tabpanel" id="panel" aria-labelledby={`tab-${tab}`}>
          <SplitPane
            label="Separador entre la configuración y la previsualización"
            top={
              tab === 'design' ? (
                <div className="design__top">
                  <ComponentTree rocket={state.rocket} selectedId={state.selectedId} issues={analysis.issues} dispatch={dispatch} onEdit={editSelected} />
                  <ComponentActions rocket={state.rocket} selectedId={state.selectedId} dispatch={dispatch} onEdit={editSelected} />
                  <ComponentPalette dispatch={dispatch} />
                </div>
              ) : tab === 'config' ? (
                <ConfigsTab
                  rocket={state.rocket}
                  motors={state.motors}
                  configs={state.configs}
                  activeId={state.activeConfigId}
                  dispatch={dispatch}
                  onPick={(configId, mountId) => setModal({ kind: 'motor-picker', configId, mountId })}
                  onRename={(id) => setModal({ kind: 'rename-config', id })}
                  onEditComponent={(id) => {
                    dispatch({ type: 'select', id });
                    editSelected();
                  }}
                />
              ) : (
                <SimulationsTab
                  state={state}
                  selectedIds={selectedIds}
                  analysis={analysis}
                  fmt={fmt}
                  running={running}
                  failure={failure}
                  onSelect={selectSim}
                  onNew={newSim}
                  onEdit={(id) => {
                    setPickedSims([id]);
                    anchor.current = id;
                    setModal({ kind: 'sim', id });
                  }}
                  onDelete={(ids) => {
                    for (const id of ids) dispatch({ type: 'sim-remove', id });
                    setPickedSims([]);
                  }}
                  onRun={(ids) => void onRun(ids)}
                  onResults={(id) => setModal({ kind: 'results', id })}
                  onCancel={cancelRun}
                />
              )
            }
            // La previsualización es la misma en las tres pestañas: solo cambia la mitad de arriba.
            bottom={
              <DesignView
                rocket={state.rocket}
                analysis={analysis}
                motors={state.motors}
                configs={state.configs}
                activeConfigId={state.activeConfigId}
                selectedId={state.selectedId}
                fmt={fmt}
                dispatch={dispatch}
                onSelect={(id) => dispatch({ type: 'select', id })}
                onEdit={editSelected}
                flight={flight}
              />
            }
          />
        </div>
      </main>

      <footer className="statusbar">
        <p>Clic para seleccionar un componente, en la lista o en el dibujo. Doble clic para editar sus propiedades.</p>
        <p>Todo se calcula en tu navegador. Implementación independiente, no afiliada a OpenRocket.</p>
      </footer>

      {editing && tab === 'design' && (
        <FloatingDialog title="Editar componente" onClose={() => setEditing(false)}>
          <PropertyPanel rocket={state.rocket} selectedId={state.selectedId} issues={analysis.issues} motors={state.motors} fmt={fmt} dispatch={dispatch} />
          <StabilityPanel analysis={analysis} fmt={fmt} />
        </FloatingDialog>
      )}

      {modal?.kind === 'sim' && modalSim !== undefined && (
        <ModalDialog title="Editar simulación" onClose={closeModal}>
          <SimulationEditor
            sim={modalSim}
            analysis={analysis}
            fmt={fmt}
            running={busy}
            configOptions={[{ value: '', label: `La activa (${configLabel(activeConfig, state.rocket, state.motors)})` }, ...state.configs.map((c) => ({ value: c.id, label: configLabel(c, state.rocket, state.motors) }))]}
            dispatch={dispatch}
            onRun={() => {
              setModal(undefined);
              goTab('sims');
              void onRun([modalSim.id]);
            }}
          />
        </ModalDialog>
      )}
      {modal?.kind === 'results' && modalSim?.run !== undefined && (
        <ModalDialog title={`Resultados: ${simLabel(modalSim)}`} onClose={closeModal} className="dialog--wide">
          <Results fileName={simLabel(modalSim)} result={modalSim.run.result} fmt={fmt} stale={isStale(state, modalSim)} />
        </ModalDialog>
      )}
      {modal?.kind === 'motor-picker' && pickerConfig !== undefined && pickerMount !== undefined && (
        <ModalDialog title="Seleccionar motor" onClose={closeModal} className="dialog--wide">
          <MotorPicker
            motors={state.motors}
            fmt={fmt}
            current={pickerConfig.motors[pickerMount.id]}
            mountName={pickerMount.name}
            dispatch={dispatch}
            onAccept={(motorId, delay) => {
              dispatch({ type: 'config-set-motor', id: pickerConfig.id, mountId: pickerMount.id, motor: { motorId, delay } });
              setModal(undefined);
            }}
          />
        </ModalDialog>
      )}
      {modal?.kind === 'rename-config' && renamed !== undefined && (
        <ModalDialog title="Renombrar configuración" onClose={closeModal}>
          <RenameConfig
            name={renamed.name ?? ''}
            onSubmit={(name) => {
              dispatch({ type: 'config-rename', id: renamed.id, name });
              setModal(undefined);
            }}
          />
        </ModalDialog>
      )}
      {modal?.kind === 'prefs' && (
        <ModalDialog title="Preferencias" onClose={closeModal}>
          <Preferences units={state.units} theme={state.theme} dispatch={dispatch} />
        </ModalDialog>
      )}
      {modal?.kind === 'about' && (
        <ModalDialog title="Acerca de" onClose={closeModal} className="dialog--wide">
          <About />
        </ModalDialog>
      )}
    </div>
  );
}
